import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { AppLayout } from 'components/AppLayout';
import { Button } from 'components/Button';
import {
  OrganizationScopeContext,
  type OrganizationScopeContextValue,
  type OrganizationSwitchOptions,
  type OrganizationSwitchStatus,
} from './context';
import { axios, ORGANIZATION_HEADER } from 'configs';
import {
  getQueryOrganizationId,
  setQueryOrganizationId,
} from 'configs/queryClient';
import { commonStrings, organizationStrings } from 'constants/strings';
import { parseOrganizationIdParam, routes } from 'constants/routes';
import { useAuth } from 'contexts/AuthContext';
import { ME_QUERY_KEY, useMe } from 'hooks/useMe';
import { runLegacyOrganizationStorageMigration } from 'utils/legacyStorageMigration';
import { resolveSwitchTargetPath } from './switchTargetPath';
import {
  draftMessageStorageKey,
  writeLastOrganizationId,
} from 'utils/organizationStorage';
import useMessageStore from 'store/useMessageStore';
import { FriendAnalyticsCollectSnackbar } from 'pages/FriendAnalytics/FriendAnalyticsCollectSnackbar';

/**
 * /o/:orgId 配下の組織スコープ Layout Route。
 * 所属検証・X-Organization-Id ヘッダー・組織切り替え・スコープ内コンテキストを提供する。
 */
export function OrganizationScope() {
  const { orgId } = useParams();
  const organizationId = parseOrganizationIdParam(orgId);
  const { user } = useAuth();
  const { data: me, isLoading, refetch, isFetching } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isBusy, setIsBusy] = useState(false);
  // busy は組織ごとに数える。前組織の操作が遅れて終わっても新組織の件数には触れない。
  const busyCountsRef = useRef(new Map<number, number>());
  const currentOrganizationIdRef = useRef(organizationId);
  currentOrganizationIdRef.current = organizationId;
  // 組織が変わった render で、その組織の実行中件数から isBusy を引き直す
  const [busyOrganizationId, setBusyOrganizationId] = useState<number | null>(
    organizationId,
  );
  if (busyOrganizationId !== organizationId) {
    setBusyOrganizationId(organizationId);
    const nextIsBusy =
      organizationId !== null &&
      (busyCountsRef.current.get(organizationId) ?? 0) > 0;
    if (isBusy !== nextIsBusy) {
      setIsBusy(nextIsBusy);
    }
  }
  const unsavedInputsRef = useRef(
    new Map<() => boolean, (() => void) | undefined>(),
  );

  // 子コンポーネントの effect は親より先に走るため、effect でのヘッダー設定では
  // 初回リクエストが旧組織ヘッダーで飛ぶ。render 中に設定して順序を保証する。
  // クエリキャッシュの組織スコープも同じ render 中に決める（別組織のキャッシュを
  // 読ませないため、子の useQuery 登録より前に hash 用の組織 ID を固定する）。
  if (organizationId !== null) {
    axios.defaults.headers.common[ORGANIZATION_HEADER] = String(organizationId);
  }
  setQueryOrganizationId(organizationId);
  // StrictMode の擬似アンマウント後も子の passive effect より先に戻すため layout effect にする
  useLayoutEffect(() => {
    if (organizationId === null) return;
    const headerValue = String(organizationId);
    axios.defaults.headers.common[ORGANIZATION_HEADER] = headerValue;
    setQueryOrganizationId(organizationId);
    return () => {
      if (axios.defaults.headers.common[ORGANIZATION_HEADER] === headerValue) {
        delete axios.defaults.headers.common[ORGANIZATION_HEADER];
      }
      if (getQueryOrganizationId() === organizationId) {
        setQueryOrganizationId(null);
      }
    };
  }, [organizationId]);

  const membership =
    organizationId !== null
      ? me?.memberships.find((m) => m.organizationId === organizationId)
      : undefined;

  const uid = user?.uid;
  const scopedOrganizationId = membership?.organizationId ?? null;
  const previousOrganizationIdRef = useRef<number | null>(null);
  // 旧キーの下書きを移してから下の effect で読み込む（同じコミットの effect は宣言順に走る）
  useEffect(() => {
    if (uid && me) {
      runLegacyOrganizationStorageMigration(uid, me.memberships);
    }
  }, [uid, me]);

  // switchOrganization を通らない遷移（戻る・進む・組織選択・通知タップ）でも
  // 前組織の Zustand 状態を残さない。子のアンマウント時ドラフト保存（旧組織キー）を
  // 消さないよう、reset はコミット後の effect で行う。
  useEffect(() => {
    if (scopedOrganizationId === null) return;
    if (previousOrganizationIdRef.current !== scopedOrganizationId) {
      previousOrganizationIdRef.current = scopedOrganizationId;
      useMessageStore.getState().resetStore();
    }
    if (uid) {
      writeLastOrganizationId(uid, scopedOrganizationId);
      useMessageStore
        .getState()
        .initDrafts(draftMessageStorageKey(uid, scopedOrganizationId));
    }
  }, [uid, scopedOrganizationId]);

  // 開始した render の組織に束縛する。戻る/進むで組織が変わった後に呼ばれても自組織だけ数える
  const beginBusy = useCallback(() => {
    if (organizationId === null) return;
    const counts = busyCountsRef.current;
    counts.set(organizationId, (counts.get(organizationId) ?? 0) + 1);
    if (currentOrganizationIdRef.current === organizationId) {
      setIsBusy(true);
    }
  }, [organizationId]);

  const endBusy = useCallback(() => {
    if (organizationId === null) return;
    const counts = busyCountsRef.current;
    const next = Math.max(0, (counts.get(organizationId) ?? 0) - 1);
    if (next === 0) {
      counts.delete(organizationId);
    } else {
      counts.set(organizationId, next);
    }
    if (next === 0 && currentOrganizationIdRef.current === organizationId) {
      setIsBusy(false);
    }
  }, [organizationId]);

  const registerUnsavedInputCheck = useCallback(
    (check: () => boolean, discard?: () => void) => {
      unsavedInputsRef.current.set(check, discard);
      return () => {
        unsavedInputsRef.current.delete(check);
      };
    },
    [],
  );

  const switchOrganization = useCallback(
    async (
      targetOrganizationId: number,
      options?: OrganizationSwitchOptions,
    ): Promise<OrganizationSwitchStatus> => {
      const busyCount =
        organizationId === null
          ? 0
          : (busyCountsRef.current.get(organizationId) ?? 0);
      if (busyCount > 0 || queryClient.isMutating() > 0) {
        return 'busy';
      }
      if (!options?.discardUnsavedInput) {
        const hasUnsavedInput = Array.from(
          unsavedInputsRef.current.keys(),
        ).some((check) => check());
        if (hasUnsavedInput) {
          return 'unsaved-input';
        }
      }

      await queryClient.cancelQueries();
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== ME_QUERY_KEY[0],
      });
      if (options?.discardUnsavedInput) {
        // 破棄に同意したときだけ、入力側のアンマウント時保存を止めてからストアを消す。
        // 同意がない経路は OrganizationScope の組織変更 effect がアンマウント後に
        // reset するので、ここで消すと旧組織キーへの下書き保存が消えてしまう。
        unsavedInputsRef.current.forEach((discard) => discard?.());
        useMessageStore.getState().resetStore();
      }
      if (uid) {
        writeLastOrganizationId(uid, targetOrganizationId);
      }
      navigate(resolveSwitchTargetPath(targetOrganizationId, options?.path));
      return 'switched';
    },
    [navigate, organizationId, queryClient, uid],
  );

  const contextValue = useMemo<OrganizationScopeContextValue | null>(
    () =>
      organizationId === null
        ? null
        : {
            organizationId,
            isBusy,
            beginBusy,
            endBusy,
            registerUnsavedInputCheck,
            switchOrganization,
          },
    [
      organizationId,
      isBusy,
      beginBusy,
      endBusy,
      registerUnsavedInputCheck,
      switchOrganization,
    ],
  );

  if (organizationId === null) {
    return <Navigate to={routes.SELECT_ORGANIZATION} replace />;
  }

  if (isLoading || (uid && isFetching && !me)) {
    return <p>{commonStrings.loadingEllipsis}</p>;
  }

  // 背景再取得の失敗（isError）はキャッシュ済みの me がある限り画面を保つ
  if (!me) {
    return (
      <div role="alert" className="p-6 text-sm text-danger">
        <p>{organizationStrings.loadFailed}</p>
        <Button type="button" size="sm" variant="outline" onClick={() => refetch()}>
          {commonStrings.actions.retry}
        </Button>
      </div>
    );
  }

  if (!membership) {
    return (
      <Navigate
        to={routes.SELECT_ORGANIZATION}
        replace
        state={{ notAvailable: true }}
      />
    );
  }

  return (
    <OrganizationScopeContext.Provider value={contextValue}>
      <AppLayout key={organizationId}>
        <FriendAnalyticsCollectSnackbar />
        <Outlet />
      </AppLayout>
    </OrganizationScopeContext.Provider>
  );
}
